import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { Switch } from './ui/switch';
import { 
  FileSpreadsheet, CheckCircle, AlertCircle, Loader2, ExternalLink, 
  Trash2, RefreshCw, Plus, Info
} from 'lucide-react';
import { toast } from 'sonner';

const GoogleSheetsSettings = ({ campaignId, campaignName }) => {
  const { isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [sheetConfig, setSheetConfig] = useState(null);
  const [customSheetName, setCustomSheetName] = useState(`${campaignName} - Registrations`);
  const [autoSync, setAutoSync] = useState(true);

  useEffect(() => {
    fetchData();
  }, [campaignId]);

  useEffect(() => {
    setCustomSheetName(`${campaignName} - Registrations`);
  }, [campaignName]);

  const fetchData = async () => {
    try {
      const [statusRes, configRes] = await Promise.all([
        api.get('/integrations/status'),
        api.get(`/integrations/sheets/config/${campaignId}`)
      ]);
      
      setIntegrationStatus(statusRes.data.google_sheets);
      setSheetConfig(configRes.data);
    } catch (error) {
      console.error('Failed to load sheets settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSheet = async () => {
    if (!customSheetName.trim()) {
      toast.error('Please enter a name for the spreadsheet');
      return;
    }
    
    setCreating(true);
    try {
      const response = await api.post('/integrations/sheets/create-campaign-sheet', {
        campaign_id: campaignId,
        sheet_name: customSheetName,
        auto_sync: autoSync
      });
      
      toast.success('Google Sheet created successfully! Check your email for access.');
      setSheetConfig({
        configured: true,
        spreadsheet_id: response.data.spreadsheet_id,
        spreadsheet_url: response.data.spreadsheet_url,
        spreadsheet_title: customSheetName,
        auto_sync: autoSync
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create sheet');
    } finally {
      setCreating(false);
    }
  };

  const handleDisableSync = async () => {
    if (!window.confirm('Are you sure you want to disable Google Sheets sync? The sheet will not be deleted.')) {
      return;
    }
    
    setDisabling(true);
    try {
      await api.delete(`/integrations/sheets/campaign/${campaignId}`);
      toast.success('Google Sheets sync disabled');
      setSheetConfig({ configured: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable sync');
    } finally {
      setDisabling(false);
    }
  };

  const handleSyncAll = async () => {
    if (!sheetConfig?.spreadsheet_id) {
      toast.error('No spreadsheet configured');
      return;
    }
    
    setSyncing(true);
    try {
      const response = await api.post('/integrations/sheets/sync', {
        campaign_id: campaignId,
        spreadsheet_id: sheetConfig.spreadsheet_id,
        sheet_name: 'Registrations'
      });
      
      toast.success(`Synced ${response.data.synced}/${response.data.total} registrations`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  // Check if Google Sheets is configured globally
  if (!integrationStatus?.configured) {
    return (
      <Card className="p-6">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-amber-100">
            <AlertCircle className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 className="text-lg font-outfit font-semibold text-slate-900">Google Sheets Not Configured</h3>
            <p className="text-sm text-slate-600 mt-1">
              Google Sheets integration needs to be set up first by a Super Admin.
            </p>
            {isSuperAdmin() && (
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => window.location.href = '/integrations'}
              >
                Go to Integrations Settings
              </Button>
            )}
          </div>
        </div>
      </Card>
    );
  }

  // Sheet is configured for this campaign
  if (sheetConfig?.configured && sheetConfig?.spreadsheet_id) {
    return (
      <div className="space-y-6">
        {/* Connected Status */}
        <Card className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <FileSpreadsheet className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-outfit font-semibold text-slate-900">
                  Google Sheets Connected
                </h3>
                <p className="text-sm text-slate-500">
                  {sheetConfig.sheet_name || 'Registrations'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-green-600">
              <CheckCircle className="w-4 h-4" />
              Active
            </div>
          </div>
          
          {/* Sheet Info */}
          <div className="mt-4 p-4 bg-slate-50 rounded-lg">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Spreadsheet Name</p>
                <p className="font-medium text-slate-900">
                  {sheetConfig.spreadsheet_name || sheetConfig.sheet_name || '—'}
                </p>
              </div>
              <div>
                <p className="text-slate-500">Auto-sync</p>
                <p className="font-medium text-slate-900">
                  {sheetConfig.auto_sync ? 'Enabled' : 'Disabled'}
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3 mt-4">
            <Button
              onClick={() => window.open(sheetConfig.spreadsheet_url, '_blank')}
              className="bg-primary hover:bg-primary-hover"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open Spreadsheet
            </Button>
            
            <Button
              variant="outline"
              onClick={handleSyncAll}
              disabled={syncing}
            >
              {syncing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Sync All Registrations
            </Button>
            
            <Button
              variant="outline"
              onClick={handleDisableSync}
              disabled={disabling}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
            >
              {disabling ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Disable Sync
            </Button>
          </div>
        </Card>

        {/* Info */}
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">How it works:</p>
              <ul className="mt-1 space-y-1 list-disc list-inside">
                <li>New registrations are automatically added to the sheet</li>
                <li>Use "Sync All" to refresh existing registrations</li>
                <li>The sheet has been shared with your email</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // No sheet configured - show setup form
  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-start gap-3 mb-6">
          <div className="p-2 rounded-lg bg-slate-100">
            <FileSpreadsheet className="w-5 h-5 text-slate-600" />
          </div>
          <div>
            <h3 className="text-lg font-outfit font-semibold text-slate-900">
              Setup Google Sheets Sync
            </h3>
            <p className="text-sm text-slate-500">
              Create a Google Sheet to automatically sync registrations
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium text-slate-700">Spreadsheet Name</Label>
            <Input
              value={customSheetName}
              onChange={(e) => setCustomSheetName(e.target.value)}
              placeholder="Enter spreadsheet name"
              className="mt-1"
            />
            <p className="text-xs text-slate-500 mt-1">
              This will be the name of the Google Sheet created for this campaign
            </p>
          </div>

          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <Label className="text-sm font-medium text-slate-700">Auto-sync New Registrations</Label>
              <p className="text-xs text-slate-500">Automatically add new registrations to the sheet</p>
            </div>
            <Switch
              checked={autoSync}
              onCheckedChange={setAutoSync}
            />
          </div>
        </div>

        <Button
          onClick={handleCreateSheet}
          disabled={creating || !customSheetName.trim()}
          className="mt-6 bg-primary hover:bg-primary-hover"
        >
          {creating ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Plus className="w-4 h-4 mr-2" />
          )}
          Create & Connect Sheet
        </Button>
      </Card>

      {/* Info */}
      <Card className="p-4 bg-green-50 border-green-200">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
          <div className="text-sm text-green-800">
            <p className="font-medium">What happens when you click "Create":</p>
            <ul className="mt-1 space-y-1 list-disc list-inside">
              <li>A new Google Sheet will be created with your custom name</li>
              <li>The sheet will be automatically shared with your email</li>
              <li>Headers will be added for registration data</li>
              <li>New registrations will auto-sync (if enabled)</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default GoogleSheetsSettings;
